import { assert, isNone, Maybe, usePeriodicCallback } from '@crocodille/common';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import Image, { ImageProps } from '../elements/image/image';
import Link, { LinkProps } from 'next/link';
import React, {
  cloneElement,
  createContext,
  CSSProperties,
  ReactElement,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useEventListener } from '../../hooks/use-event-listener';
import { useFocusWithin } from '../../hooks/use-focus-within';
import {
  IntersectionObserverContainer,
  useIntersectionObserver,
} from '../../hooks/use-intersection-observer';
import { useIsomorphicLayoutEffect as useLayoutEffect } from '../../hooks/use-isomorphic-layout-effect';
import { useScrollState } from '../../hooks/use-scroll-state';
import styles from './carousel.module.scss';

interface SlidesContext {
  readonly visibleSlideIndex: number;
  readonly slideCount: number;
}

const SlidesContext = createContext<SlidesContext>({
  visibleSlideIndex: 0,
  slideCount: 0,
});

/**
 * Given a number and size, returns the number modulo size. However negative
 * numbers will never be returned and will be correctly wrapped to positive numbers.
 *
 * @param value value to wrap
 * @param size the size to wrap into
 *
 * @returns wrapped number
 */
function wrapping(value: number, size: number): number {
  value = value % size;

  if (value < 0) {
    return size + value;
  }

  return value;
}

/**
 * Expands list of slides in a way that there's an additional element at the beginning and
 * additional `shownSlideCount` elements at the end.
 * These elements are last and first from the original list. Both for purposes of wrapping scroll.
 *
 * @param slides Slide element list
 *
 * @returns Expanded list
 */
function expandSlides(
  slides: readonly ReactElement<SlideProps, typeof Slide>[],
  shownSlideCount = 1,
): readonly ReactElement<SlideProps, typeof Slide>[] {
  assert(slides.length > 0);
  assert(shownSlideCount > 0);

  //
  // The minimal expanded length is one additional slide at the beginning,
  // plus at least `shownSlideCount` slides at the end.
  //
  const minExpandedLength = slides.length + 1 + shownSlideCount;
  const expanded = [cloneElement(slides[slides.length - 1])];

  while (expanded.length < minExpandedLength) {
    for (const slide of slides) {
      expanded.push(cloneElement(slide));
    }
  }

  return [...expanded, cloneElement(slides[0])];
}

function elementIndex(el: Maybe<HTMLElement>): Maybe<number> {
  if (isNone(el)) {
    return null;
  }

  const index = Array.from(el?.parentElement?.children ?? []).indexOf(el);

  return index === -1 ? null : index;
}

export interface CarouselProps {
  readonly children: readonly ReactElement<SlideProps, typeof Slide>[];
  readonly shownSlideCount?: number;
  readonly autoAdvanceInterval?: number;
  readonly label: string;
  readonly className?: string;
}

/**
 * Carousel component implemented as a horizontally scrolling element.
 *
 * The element has hidden scrollbar and snapping scroll. The browser is responsible for scrolling.
 * The component only observes current scroll position and using that determines which slide is shown.
 * To show a specific slide, the component instructs browser to scroll to a specific offset.
 */
const Carousel: React.FC<React.PropsWithChildren<CarouselProps>> = ({
  children,
  shownSlideCount = 1,
  autoAdvanceInterval = 3000,
  label,
  className,
}) => {
  //
  // Expand provided slides
  //
  const slides = useMemo(
    () => expandSlides(children, shownSlideCount),
    [children, shownSlideCount],
  );

  const listRef = useRef<HTMLDivElement>(null);
  //
  // Represents leftmost visible slide. (updated when it's fully visible)
  // We have more slides slides thanks to expansion.
  // The first shown slide should be second, since first is only for scroll wrap.
  //
  const [visibleSlideIndex, setVisibleSlideIndex] = useState(1);

  const slideCount = slides.length;

  const revealSlide = useCallback(
    (index: number, instantly = false) => {
      if (slideCount === 0) {
        return;
      }

      const slideEl = listRef.current?.children?.[index] as Maybe<HTMLElement>;

      assert(slideEl);

      listRef.current?.scroll?.({
        left: slideEl?.offsetLeft,
        behavior: instantly ? 'auto' : 'smooth',
      });
    },
    [slideCount],
  );

  const next = useCallback(
    () =>
      slideCount && revealSlide(wrapping(visibleSlideIndex + 1, slideCount)),
    [revealSlide, slideCount, visibleSlideIndex],
  );

  //
  // Initially, move the scroll position to the first slide.
  //
  useLayoutEffect(() => {
    revealSlide(shownSlideCount, true);
  }, [shownSlideCount, revealSlide, children.length]);

  //
  // Scroll position is changed to corresponding slide if it reaches end.
  //
  useEventListener(
    'scroll',
    () => {
      const list = listRef.current;

      if (!list) {
        return;
      }

      //
      // We reached the slide added to the left side as wrapping (last slide of the original slide list)
      // So we transition to the last slide which was an actual child.
      //
      if (list.scrollLeft <= 0) {
        revealSlide(children.length, true);
      }

      //
      // We reached the end of the scroll ...
      // We transition to the second slide since first is wrapping buffer.
      // The target slide also needs to be adjusted by `shownSlideCount - 1` to the left
      // since we know the right-most slide was the last one, but we reveal the left-most slide.
      //
      if (list.scrollLeft >= list.scrollWidth - list.clientWidth) {
        revealSlide(wrapping(1 - (shownSlideCount - 1), children.length), true);
      }
    },
    listRef,
    { passive: true },
  );

  const carouselRef = useRef<HTMLElement>(null);

  const isFocused = useFocusWithin(carouselRef);
  const [isHovered, setIsHovered] = useState(false);
  const isScrolling = useScrollState(listRef, 500);

  const autoAdvanceEnabled = !isHovered && !isFocused && !isScrolling;

  usePeriodicCallback(
    (index) => {
      if (index > 0) {
        next();
      }
    },
    {
      period: autoAdvanceInterval,
      enabled: autoAdvanceEnabled,
    },
  );

  const visibleSlideIndices = useRef<Set<number>>(new Set());

  const intersectionCallback = (
    entries: readonly IntersectionObserverEntry[],
  ) => {
    const children = Array.from(listRef.current?.children ?? []);
    for (const entry of entries) {
      const slideIndex = children.indexOf(entry.target);
      assert(slideIndex !== -1);

      if (entry.isIntersecting) {
        visibleSlideIndices.current.add(slideIndex);
        setVisibleSlideIndex(
          Math.min(...Array.from(visibleSlideIndices.current)),
        );
      } else {
        visibleSlideIndices.current.delete(slideIndex);
      }
    }
  };

  const slideContext = useMemo(
    () => ({
      visibleSlideIndex,
      slideCount: children.length,
    }),
    [children.length, visibleSlideIndex],
  );

  const style = useMemo(
    () => ({ ['--shown-slide-count']: shownSlideCount.toString() }),
    [shownSlideCount],
  );

  const controls: React.ReactNode[] = [];

  React.Children.forEach(children, (slide, index) => {
    if (!slide) {
      return;
    }

    controls.push(
      <button
        key={index - 1}
        className={cn(styles.control, {
          [styles.isActive]:
            index === (visibleSlideIndex - 1) % React.Children.count(children),
        })}
        type="button"
        onClick={() => revealSlide(index + 1)}
      >
        {slide.props.label}
      </button>,
    );
  });

  return (
    <section
      ref={carouselRef}
      className={cn(styles.container, className)}
      style={style as CSSProperties}
      role="region"
      aria-roledescription="carousel"
      aria-label={label}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <IntersectionObserverContainer
        ref={listRef}
        callback={intersectionCallback}
        threshold={0.95}
      >
        <div
          id="carousel-list"
          className={cn(styles.list)}
          aria-live={isFocused ? 'polite' : 'off'}
        >
          <SlidesContext.Provider value={slideContext}>
            {slides.map((slide, index) => (
              <React.Fragment key={index - 1}>{slide}</React.Fragment>
            ))}
          </SlidesContext.Provider>
        </div>
      </IntersectionObserverContainer>
      <div className={styles.controls}>{controls}</div>
    </section>
  );
};

export type SlideProps = Pick<ImageProps, 'src'> & {
  readonly href: LinkProps['href'] | null;
  readonly label: string;
  readonly className?: string;
};

const Slide: React.FC<React.PropsWithChildren<SlideProps>> = ({
  src,
  label,
  href,
  className,
}) => {
  const elementRef = useRef<HTMLDivElement>(null);

  useIntersectionObserver(elementRef);

  const element = elementRef.current;
  const slideIndex = useMemo(() => elementIndex(element) ?? 0, [element]);

  const { visibleSlideIndex, slideCount } = useContext(SlidesContext);

  const { t } = useTranslation();

  const displayIndex = wrapping(slideIndex - 1, slideCount) + 1;
  const isVisible = visibleSlideIndex === slideIndex;

  return (
    <div
      ref={elementRef}
      className={cn(styles.slide, className)}
      aria-label={t('carousel.slide_label', {
        index: displayIndex,
        total: slideCount,
      })}
      aria-hidden={!isVisible}
      role="group"
      aria-roledescription="slide"
    >
      {href ? (
        <Link
          href={href}
          prefetch={false}
          className={cn(styles.slideLink)}
          tabIndex={isVisible ? 0 : -1}
        >
          <Image
            src={src}
            alt={label}
            layout="fill"
            objectFit="cover"
            priority={isVisible}
            loading={isVisible ? 'eager' : 'lazy'}
          />
        </Link>
      ) : (
        <div className={cn(styles.slideLink)} tabIndex={isVisible ? 0 : -1}>
          <Image
            src={src}
            alt={label}
            layout="fill"
            objectFit="cover"
            priority={isVisible}
            loading={isVisible ? 'eager' : 'lazy'}
          />
        </div>
      )}
    </div>
  );
};

export default Object.assign(Carousel, { Slide });
